/**
 * Inventaire: fonctions pures. Aucune ne modifie son argument, chacune rend un
 * nouvel objet. C'est ce qui permet au serveur de rejouer une action et de
 * comparer les deux inventaires sans copie defensive ailleurs.
 * Pas de limite de place ni de poids: on n'enleve jamais rien au joueur.
 */
import type { Cout, Inventaire, ItemDef, ItemId } from '../types/items.ts'
import { OBJETS } from '../data/objets.ts'

const ORDRE_CATEGORIE: Record<ItemDef['categorie'], number> = {
  matiere: 0,
  nourriture: 1,
  objet: 2,
  outil: 3,
}

/**
 * Cle de tri sans accents. On n'utilise pas localeCompare: son resultat depend
 * de l'ICU embarque, donc le client et le serveur pourraient ne pas trier
 * pareil.
 */
function cleNom(nom: string): string {
  return nom.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

/**
 * UN SAC QUI NE MANQUE JAMAIS DE RIEN — pour le decor du monde, qui ne se paie
 * pas.
 *
 * Il etait ecrit a la main a trois endroits, avec cinq matieres sur vingt-deux:
 * `{ bois, pierre, fibre, sable, minerai }`. Tant que le decor ne comptait
 * qu'une cabane et un aerodrome, cela suffisait; le jour ou l'on a voulu poser
 * un jardin (qui coute des FEUILLES) et un chalet, `validerPlacement` a repondu
 * « Il faut 2 feuille » a un batiment qui n'appartient a personne et ne paie
 * rien. « Une liste recopiee finit toujours par etre incomplete »: celle-ci se
 * deduit de la table des objets, donc elle ne peut plus rater une matiere.
 */
export const SAC_DU_MONDE: Inventaire = Object.fromEntries(
  (Object.keys(OBJETS) as ItemId[]).map((id) => [id, 9999]),
) as Inventaire

export function quantite(inv: Inventaire, id: ItemId): number {
  return inv[id] ?? 0
}

export function ajouter(inv: Inventaire, id: ItemId, n: number): Inventaire {
  if (n === 0) return inv
  const suivant: Inventaire = { ...inv }
  const total = (suivant[id] ?? 0) + n
  if (total <= 0) delete suivant[id]
  else suivant[id] = total
  return suivant
}

export function ajouterLot(inv: Inventaire, lot: Cout): Inventaire {
  const suivant: Inventaire = { ...inv }
  for (const cle of Object.keys(lot) as ItemId[]) {
    const n = lot[cle] ?? 0
    if (n === 0) continue
    const total = (suivant[cle] ?? 0) + n
    if (total <= 0) delete suivant[cle]
    else suivant[cle] = total
  }
  return suivant
}

export function peutPayer(inv: Inventaire, cout: Cout): boolean {
  for (const cle of Object.keys(cout) as ItemId[]) {
    if ((inv[cle] ?? 0) < (cout[cle] ?? 0)) return false
  }
  return true
}

/** Rend le nouvel inventaire, ou null si les ressources manquent. */
export function payer(inv: Inventaire, cout: Cout): Inventaire | null {
  if (!peutPayer(inv, cout)) return null
  const suivant: Inventaire = { ...inv }
  for (const cle of Object.keys(cout) as ItemId[]) {
    const reste = (suivant[cle] ?? 0) - (cout[cle] ?? 0)
    if (reste <= 0) delete suivant[cle]
    else suivant[cle] = reste
  }
  return suivant
}

export function retirer(inv: Inventaire, id: ItemId, n: number): Inventaire {
  return ajouter(inv, id, -n)
}

export function total(inv: Inventaire): number {
  let somme = 0
  for (const cle of Object.keys(inv) as ItemId[]) somme += inv[cle] ?? 0
  return somme
}

/** Ce qui manque pour payer, a afficher en rouge. Vide si on peut payer. */
export function manquant(inv: Inventaire, cout: Cout): Cout {
  const trou: Cout = {}
  for (const cle of Object.keys(cout) as ItemId[]) {
    const besoin = cout[cle] ?? 0
    const dispo = inv[cle] ?? 0
    if (dispo < besoin) trou[cle] = besoin - dispo
  }
  return trou
}

/** Contenu trie par categorie puis par nom, pour l'affichage. */
export function trier(inv: Inventaire): { id: ItemId; n: number }[] {
  const lignes: { id: ItemId; n: number }[] = []
  for (const cle of Object.keys(inv) as ItemId[]) {
    const n = inv[cle] ?? 0
    if (n > 0) lignes.push({ id: cle, n })
  }
  lignes.sort((a, b) => {
    const da = OBJETS[a.id]
    const db = OBJETS[b.id]
    const ca = ORDRE_CATEGORIE[da.categorie]
    const cb = ORDRE_CATEGORIE[db.categorie]
    if (ca !== cb) return ca - cb
    const na = cleNom(da.nom)
    const nb = cleNom(db.nom)
    return na < nb ? -1 : na > nb ? 1 : 0
  })
  return lignes
}
